import { FadeIn } from "@xola/ui-kit";
import clsx from "clsx";
import { getPartnerFee, isAdjustmentLineItem } from "common";
import { pick, round } from "lodash";
import React from "react";
import {
    Experience,
    LineItem,
    LineItemType,
    LineItemVisibility,
    Package,
    PartnerPackage,
    Purchase,
    PurchaseItem,
} from "types";
import { useAutoFetchCollection } from "../../hooks/useAutoFetchCollection";
import { useResource } from "../../hooks/useResource";
import { Breakdown } from "../Common/Breakdown";
import { Currency } from "../Common/Currency";
import { CreatePurchaseSidebarSkeleton } from "./CreatePurchaseSidebarSkeleton";
import { getProductIdsInPackage } from "@/models/Package";

interface LineItemDetails
    extends Pick<LineItem, "name" | "price" | "amount" | "quantity" | "type" | "includeInAmount"> {}

export const getPackageBreakdown = (purchase: Purchase, experiences: Experience[]) => {
    const summary: { [key: string]: LineItemDetails } = {};
    for (const item of purchase.items) {
        for (const lineItem of getItemLineItems(item)) {
            const experience = experiences.find(({ id }) => id === item.product.id);
            let foundSummary = summary[lineItem.template?.id];
            if (lineItem.type === LineItemType.COUPON_DISCOUNT && lineItem.coupon) {
                foundSummary = summary[lineItem.coupon.id];
            }

            if (foundSummary) {
                foundSummary.price = foundSummary?.price ?? 0;
                if (lineItem.price) {
                    foundSummary.price += lineItem.price;
                }

                foundSummary.amount += lineItem.amount;
            } else {
                const values = pick(lineItem, ["name", "price", "amount", "quantity", "type", "includeInAmount"]);
                const isAdjustment = isAdjustmentLineItem(lineItem);
                const isAddon = values.type === LineItemType.ADDON;
                const isPickup = values.type === LineItemType.PICKUP;
                let key = isAdjustment ? lineItem.id : lineItem.template?.id;
                if (lineItem.type === LineItemType.COUPON_DISCOUNT && lineItem.coupon) {
                    // group by different coupons ids
                    key = lineItem.coupon.id;
                }

                const showBreakdownPerListing =
                    isAddon ||
                    isAdjustment ||
                    isPickup ||
                    ([LineItemType.FEE, LineItemType.TAX].includes(values.type) && !values.includeInAmount);
                if (showBreakdownPerListing) {
                    values.name = `${experience?.name}: ${values.name}`;
                    key += experience?.id ?? "";
                }

                summary[key] = values;
            }
        }
    }

    return summary;
};

const getItemLineItems = (item: PurchaseItem) => {
    return item.lineItems.filter((lineItem) => {
        if (
            [LineItemType.DEMOGRAPHIC, LineItemType.PACKAGE_DISCOUNT, LineItemType.PARTNER_PACKAGE_DISCOUNT].includes(
                lineItem.type,
            ) &&
            lineItem.quantity === 0
        ) {
            return false;
        }

        if (lineItem.type === LineItemType.PACKAGE_DISCOUNT && lineItem.amount === 0) {
            return false;
        }

        if (lineItem.type === LineItemType.PARTNER_PACKAGE_DISCOUNT && lineItem.amount === 0) {
            return false;
        }

        if (isAdjustmentLineItem(lineItem) && lineItem.amount > 0) {
            return true;
        }

        return lineItem.visibility !== LineItemVisibility.NONE && "template" in lineItem;
    });
};

export const CreatePurchasePackageBreakdown = ({ purchase, packageId }: { purchase: Purchase; packageId: ID }) => {
    const { data: pkg, isLoading: isLoadingPackage } = useResource<Package | PartnerPackage>(["products", packageId]);
    const { data: experiences, isFetching } = useAutoFetchCollection<Experience>("experiences");

    if (isFetching || isLoadingPackage) {
        return <CreatePurchaseSidebarSkeleton />;
    }

    const experienceIds = pkg ? getProductIdsInPackage(pkg) : [];
    const packageExperiences = experiences.filter((experience) => experienceIds?.includes(experience.id));
    const breakdown = Object.entries(getPackageBreakdown(purchase, packageExperiences));
    const partnerFee = getPartnerFee(purchase)?.amount;
    const purchaseAmount = partnerFee ? round(purchase.amount - partnerFee, 2) : purchase.amount;

    return (
        <FadeIn>
            <Breakdown currency={purchase.currency}>
                {breakdown.map((lineItemBreakdown: any) => {
                    return lineItemBreakdown[1] ? (
                        <BreakdownPackageLineItem
                            key={lineItemBreakdown[0]}
                            name={lineItemBreakdown[1].name}
                            price={lineItemBreakdown[1].price}
                            quantity={lineItemBreakdown[1].quantity}
                            amount={lineItemBreakdown[1].amount}
                            type={lineItemBreakdown[1].type}
                            includeInAmount={lineItemBreakdown[1].includeInAmount}
                        />
                    ) : null;
                })}

                {/* Code from Breakdown.separator because I can't change color of the separator */}
                <tr className={clsx("ui-breakdown-separator")}>
                    <td colSpan={3} className="border-b border-black/10 pb-1" />
                </tr>

                <Breakdown.SubtotalItem info="Total" value={purchaseAmount} />
            </Breakdown>
        </FadeIn>
    );
};

const BreakdownPackageLineItem = ({ name, price, quantity, amount, type, includeInAmount }: LineItemDetails) => {
    return type === LineItemType.PACKAGE_DISCOUNT || type === LineItemType.PARTNER_PACKAGE_DISCOUNT || !price ? (
        <Breakdown.Item value={amount} className={clsx(!includeInAmount && "line-through")}>
            {name}
        </Breakdown.Item>
    ) : (
        <Breakdown.Item value={Math.abs(amount)} className={clsx(!includeInAmount && "line-through")}>
            {name} (<Currency>{price}</Currency> x {quantity ?? 1})
        </Breakdown.Item>
    );
};
